/**
 * Urologic Cancer Content - Uro2Go
 * 
 * Comprehensive patient education articles covering all urologic cancers.
 * Written at 8th-grade reading level for patient accessibility.
 * Both English and Spanish versions provided.
 */

export const urologicCancerArticles = [
  {
    id: 'prostate-cancer',
    title: {
      en: 'Prostate Cancer',
      es: 'Cáncer de Próstata'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg/600px-Diagram_showing_the_position_of_the_prostate_and_rectum_CRUK_358.svg.png',
      alt: {
        en: 'Diagram showing the position of the prostate gland',
        es: 'Diagrama que muestra la posición de la glándula prostática'
      },
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'The prostate gland sits below the bladder and surrounds the urethra',
        es: 'La glándula prostática se encuentra debajo de la vejiga y rodea la uretra'
      }
    },
    tags: ['prostate', 'psa', 'gleason', 'prostatectomy', 'radiation'],
    relatedTopics: ['prostate', 'elevated-psa', 'prostate-biopsy'],
    content: {
      en: `Prostate cancer is the most common cancer in men, aside from skin cancer. It develops in the prostate gland, a small walnut-sized organ that sits below the bladder and produces fluid for semen.

The good news is that prostate cancer often grows slowly. Many men with prostate cancer live long, healthy lives. When found early, prostate cancer has a very high survival rate.

**Who Is at Risk?**
Prostate cancer is more common in men over age 50. Your risk increases if you have a father or brother with prostate cancer. African American men have a higher risk and tend to develop more aggressive forms.

**Symptoms**
Early prostate cancer usually causes no symptoms. That's why screening with PSA blood tests is important. As it grows, symptoms may include difficulty urinating, weak urine stream, blood in urine or semen, or bone pain if cancer has spread.

**Diagnosis**
If your PSA is elevated or your doctor feels something abnormal during a rectal exam, you may need a prostate biopsy. This involves taking small tissue samples from the prostate to check for cancer cells. MRI imaging can help identify suspicious areas.

**Treatment Options**
Treatment depends on how aggressive the cancer is and your overall health:
• **Active Surveillance** – For slow-growing cancers, careful monitoring with regular tests instead of immediate treatment
• **Surgery (Prostatectomy)** – Removing the prostate gland
• **Radiation Therapy** – Using high-energy beams to kill cancer cells
• **Hormone Therapy** – Reducing testosterone that fuels cancer growth
• **Chemotherapy** – For advanced cancer that has spread

Many men have excellent outcomes with treatment. Your doctor will help you choose the best approach for your specific situation.`,
      es: `El cáncer de próstata es el cáncer más común en los hombres, aparte del cáncer de piel. Se desarrolla en la glándula prostática, un pequeño órgano del tamaño de una nuez que se encuentra debajo de la vejiga y produce líquido para el semen.

La buena noticia es que el cáncer de próstata a menudo crece lentamente. Muchos hombres con cáncer de próstata viven vidas largas y saludables. Cuando se detecta temprano, tiene una tasa de supervivencia muy alta.

**¿Quién Está en Riesgo?**
El cáncer de próstata es más común en hombres mayores de 50 años. Su riesgo aumenta si tiene un padre o hermano con cáncer de próstata. Los hombres afroamericanos tienen un mayor riesgo.

**Síntomas**
El cáncer de próstata temprano generalmente no causa síntomas. Por eso es importante el examen con análisis de sangre PSA. A medida que crece, los síntomas pueden incluir dificultad para orinar, chorro de orina débil, sangre en la orina o el semen, o dolor óseo si el cáncer se ha propagado.

**Diagnóstico**
Si su PSA está elevado o su médico siente algo anormal durante un examen rectal, puede necesitar una biopsia de próstata.

**Opciones de Tratamiento**
El tratamiento depende de qué tan agresivo sea el cáncer y su salud general:
• **Vigilancia Activa** – Monitoreo cuidadoso en lugar de tratamiento inmediato
• **Cirugía (Prostatectomía)** – Extirpación de la glándula prostática
• **Radioterapia** – Uso de rayos de alta energía para matar células cancerosas
• **Terapia Hormonal** – Reducción de la testosterona que alimenta el crecimiento del cáncer
• **Quimioterapia** – Para cáncer avanzado que se ha propagado`
    },
    keyPoints: {
      en: [
        'Most common cancer in men (aside from skin cancer)',
        'Often grows slowly with high survival rates when found early',
        'PSA blood test and rectal exam are key screening tools',
        'Treatment ranges from active surveillance to surgery or radiation',
        'Many treatment options preserve quality of life'
      ],
      es: [
        'El cáncer más común en hombres (aparte del cáncer de piel)',
        'A menudo crece lentamente con altas tasas de supervivencia',
        'El análisis de sangre PSA y el examen rectal son herramientas clave',
        'El tratamiento va desde vigilancia activa hasta cirugía o radiación',
        'Muchas opciones de tratamiento preservan la calidad de vida'
      ]
    }
  },
  {
    id: 'bladder-cancer',
    title: {
      en: 'Bladder Cancer',
      es: 'Cáncer de Vejiga'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5c/Diagram_showing_the_T_stages_of_bladder_cancer_CRUK_372.svg/600px-Diagram_showing_the_T_stages_of_bladder_cancer_CRUK_372.svg.png',
      alt: {
        en: 'Diagram showing bladder cancer stages',
        es: 'Diagrama que muestra las etapas del cáncer de vejiga'
      },
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'Bladder cancer stages showing how deep the tumor grows into the bladder wall',
        es: 'Etapas del cáncer de vejiga mostrando qué tan profundo crece el tumor'
      }
    },
    tags: ['bladder', 'hematuria', 'turbt', 'bcg', 'cystectomy'],
    relatedTopics: ['bladder', 'hematuria', 'cystoscopy', 'bcg-therapy'],
    content: {
      en: `Bladder cancer develops in the lining of the bladder, the organ that stores urine. It is one of the most common urologic cancers and is highly treatable when caught early.

Most bladder cancers (about 90%) start in the urothelial cells that line the inside of the bladder. This type is called urothelial carcinoma or transitional cell carcinoma.

**Who Is at Risk?**
• **Smoking** is the biggest risk factor – smokers are 3 times more likely to develop bladder cancer
• **Age** – most cases occur in people over 55
• **Men** are more likely to develop it than women
• Exposure to certain chemicals (dyes, rubber, leather industries)
• Chronic bladder infections or irritation

**Symptoms**
The most common warning sign is blood in the urine (hematuria). The urine may look pink, red, or cola-colored. Other symptoms include:
• Frequent urination
• Pain or burning during urination
• Feeling the need to urinate but unable to
• Lower back pain on one side

**Diagnosis**
Your doctor may recommend:
• **Cystoscopy** – A thin camera is inserted into the bladder to look directly at the lining
• **Urine tests** – Looking for cancer cells or markers
• **CT scan** – To see if cancer has spread

**Treatment Options**
Treatment depends on the stage:
• **Non-muscle invasive (early stage)** – Cancer stays in the bladder lining
  - TURBT surgery to remove tumors
  - BCG immunotherapy to prevent recurrence
  - Regular cystoscopy monitoring
• **Muscle-invasive** – Cancer has grown into bladder muscle
  - Radical cystectomy (bladder removal)
  - Radiation with chemotherapy
  - Immunotherapy

Bladder cancer has a tendency to come back, so regular follow-up is essential.`,
      es: `El cáncer de vejiga se desarrolla en el revestimiento de la vejiga, el órgano que almacena la orina. Es uno de los cánceres urológicos más comunes y es altamente tratable cuando se detecta temprano.

La mayoría de los cánceres de vejiga (alrededor del 90%) comienzan en las células uroteliales que recubren el interior de la vejiga.

**¿Quién Está en Riesgo?**
• **Fumar** es el factor de riesgo más grande
• **Edad** – la mayoría de los casos ocurren en personas mayores de 55
• **Los hombres** tienen más probabilidades de desarrollarlo que las mujeres
• Exposición a ciertos químicos

**Síntomas**
La señal de advertencia más común es sangre en la orina (hematuria). La orina puede verse rosada, roja o del color de la cola. Otros síntomas incluyen:
• Micción frecuente
• Dolor o ardor al orinar
• Sentir la necesidad de orinar pero no poder
• Dolor en la parte baja de la espalda de un lado

**Diagnóstico**
Su médico puede recomendar:
• **Cistoscopia** – Una cámara delgada para ver el interior de la vejiga
• **Análisis de orina** – Buscando células cancerosas
• **Tomografía** – Para ver si el cáncer se ha propagado

**Opciones de Tratamiento**
• **No invasivo muscular (etapa temprana)**
  - Cirugía TURBT para eliminar tumores
  - Inmunoterapia BCG para prevenir recurrencia
• **Invasivo muscular**
  - Cistectomía radical (extirpación de la vejiga)
  - Radiación con quimioterapia`
    },
    keyPoints: {
      en: [
        'Blood in urine is the most common warning sign',
        'Smoking is the biggest preventable risk factor',
        'Most cases are caught early and very treatable',
        'Regular follow-up is crucial as bladder cancer can recur',
        'BCG immunotherapy is highly effective for early-stage disease'
      ],
      es: [
        'La sangre en la orina es la señal de advertencia más común',
        'Fumar es el factor de riesgo prevenible más grande',
        'La mayoría de los casos se detectan temprano y son tratables',
        'El seguimiento regular es crucial ya que puede recurrir',
        'La inmunoterapia BCG es muy efectiva para la enfermedad temprana'
      ]
    }
  },
  {
    id: 'kidney-cancer',
    title: {
      en: 'Kidney Cancer',
      es: 'Cáncer de Riñón'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e3/Diagram_showing_stage_1_kidney_cancer_CRUK_192.svg/600px-Diagram_showing_stage_1_kidney_cancer_CRUK_192.svg.png',
      alt: {
        en: 'Diagram showing kidney cancer',
        es: 'Diagrama que muestra el cáncer de riñón'
      },
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'A kidney tumor contained within the kidney',
        es: 'Un tumor renal contenido dentro del riñón'
      }
    },
    tags: ['kidney', 'renal', 'nephrectomy', 'renal cell carcinoma'],
    relatedTopics: ['kidney', 'renal-cysts'],
    content: {
      en: `Kidney cancer develops when cells in the kidney grow abnormally and form a tumor. The most common type is called renal cell carcinoma (RCC), which accounts for about 90% of all kidney cancers.

Kidney cancer is often found by accident during imaging tests done for other reasons. Many small kidney tumors are discovered early and can be treated successfully.

**Who Is at Risk?**
• **Smoking** – doubles your risk
• **Obesity** – excess weight increases risk
• **High blood pressure**
• **Family history** of kidney cancer
• Long-term dialysis treatment
• Certain inherited conditions (von Hippel-Lindau disease)

**Symptoms**
Early kidney cancer often causes no symptoms. When symptoms do appear, they may include:
• Blood in the urine (most common)
• Pain in the side or lower back that doesn't go away
• A lump or mass in the side or abdomen
• Unexplained weight loss
• Fatigue
• Fever that comes and goes

**Diagnosis**
• **CT scan or MRI** – Shows the size and location of tumors
• **Ultrasound** – May first detect a mass
• **Biopsy** – Sometimes needed to confirm cancer type

**Treatment Options**
Treatment depends on tumor size, stage, and your overall health:
• **Surgery** – The main treatment for kidney cancer
  - Partial nephrectomy (removes only the tumor, saves healthy kidney)
  - Radical nephrectomy (removes the entire kidney)
• **Ablation** – Destroying small tumors with heat or cold
• **Active Surveillance** – Monitoring very small tumors in older patients
• **Targeted Therapy and Immunotherapy** – For advanced cancer
• **Radiation** – Sometimes used to control symptoms

The good news is that kidney cancer caught early has an excellent prognosis. Even with one kidney removed, most people live normal, healthy lives.`,
      es: `El cáncer de riñón se desarrolla cuando las células del riñón crecen anormalmente y forman un tumor. El tipo más común se llama carcinoma de células renales (CCR), que representa aproximadamente el 90% de todos los cánceres de riñón.

El cáncer de riñón a menudo se encuentra por accidente durante pruebas de imagen realizadas por otras razones.

**¿Quién Está en Riesgo?**
• **Fumar** – duplica su riesgo
• **Obesidad** – el exceso de peso aumenta el riesgo
• **Presión arterial alta**
• **Historia familiar** de cáncer de riñón
• Tratamiento de diálisis a largo plazo

**Síntomas**
El cáncer de riñón temprano a menudo no causa síntomas. Cuando aparecen, pueden incluir:
• Sangre en la orina (más común)
• Dolor en el costado o la parte baja de la espalda
• Un bulto o masa en el costado o abdomen
• Pérdida de peso inexplicable
• Fatiga
• Fiebre intermitente

**Diagnóstico**
• **Tomografía o resonancia magnética** – Muestra el tamaño y ubicación de los tumores
• **Ultrasonido** – Puede detectar primero una masa
• **Biopsia** – A veces necesaria para confirmar el tipo de cáncer

**Opciones de Tratamiento**
• **Cirugía** – El tratamiento principal
  - Nefrectomía parcial (extirpa solo el tumor)
  - Nefrectomía radical (extirpa todo el riñón)
• **Ablación** – Destrucción de tumores pequeños con calor o frío
• **Vigilancia Activa** – Monitoreo de tumores muy pequeños
• **Terapia Dirigida e Inmunoterapia** – Para cáncer avanzado`
    },
    keyPoints: {
      en: [
        'Often found incidentally during imaging for other reasons',
        'Smoking and obesity are major risk factors',
        'Surgery is the main treatment for localized tumors',
        'Many patients do well with one kidney after surgery',
        'Early detection leads to excellent outcomes'
      ],
      es: [
        'A menudo se encuentra incidentalmente durante imágenes por otras razones',
        'Fumar y la obesidad son factores de riesgo importantes',
        'La cirugía es el tratamiento principal para tumores localizados',
        'Muchos pacientes viven bien con un solo riñón después de la cirugía',
        'La detección temprana conduce a excelentes resultados'
      ]
    }
  },
  {
    id: 'testicular-cancer',
    title: {
      en: 'Testicular Cancer',
      es: 'Cáncer Testicular'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/Diagram_of_the_testicles_CRUK_048.svg/500px-Diagram_of_the_testicles_CRUK_048.svg.png',
      alt: {
        en: 'Diagram of testicular anatomy',
        es: 'Diagrama de la anatomía testicular'
      },
      source: 'Cancer Research UK / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'Anatomy of the testicle showing the epididymis and vas deferens',
        es: 'Anatomía del testículo mostrando el epidídimo y los conductos deferentes'
      }
    },
    tags: ['testicular', 'testicle', 'orchiectomy', 'germ cell'],
    relatedTopics: ['testicle', 'testicular-pain', 'scrotal-cysts'],
    content: {
      en: `Testicular cancer is the most common cancer in young men between ages 15 and 35. The encouraging news is that it is one of the most curable cancers, even when it has spread. With early treatment, the survival rate is over 95%.

Testicular cancer develops in one or both testicles, the male reproductive organs that produce sperm and testosterone. Most testicular cancers are germ cell tumors.

**Who Is at Risk?**
• **Age** – Most common between 15-35, but can occur at any age
• **Undescended testicle** (cryptorchidism) – Even after correction
• **Family history** of testicular cancer
• **Personal history** – If you had it in one testicle, higher risk in the other
• **HIV infection**

**Symptoms**
The most common sign is a painless lump or swelling in either testicle. Other symptoms include:
• A feeling of heaviness in the scrotum
• A dull ache in the lower belly or groin
• Sudden fluid collection in the scrotum
• Pain or discomfort in a testicle
• Breast tenderness or growth (rare)

**How to Check Yourself**
Monthly self-exams can help catch cancer early:
1. Do it during or after a warm shower when the scrotum is relaxed
2. Hold one testicle between your thumbs and fingers
3. Roll it gently to feel for any hard lumps or changes
4. Repeat with the other testicle
5. Normal testicles feel smooth and slightly spongy

**Diagnosis**
• **Ultrasound** – First test to examine a lump
• **Blood tests** – Tumor markers (AFP, beta-hCG, LDH)
• **Surgery (orchiectomy)** – Removal of the affected testicle for diagnosis and treatment

**Treatment**
Treatment depends on the type and stage:
• **Surgery** – Removing the affected testicle (does not affect fertility or sexual function with one testicle)
• **Surveillance** – Close monitoring after surgery for early-stage cancer
• **Chemotherapy** – Very effective for testicular cancer
• **Radiation** – For certain types
• **Retroperitoneal lymph node dissection** – Removing affected lymph nodes

Most men go on to live normal lives after treatment. Sperm banking is available for those concerned about future fertility.`,
      es: `El cáncer testicular es el cáncer más común en hombres jóvenes entre 15 y 35 años. La noticia alentadora es que es uno de los cánceres más curables, incluso cuando se ha propagado. Con tratamiento temprano, la tasa de supervivencia es superior al 95%.

**¿Quién Está en Riesgo?**
• **Edad** – Más común entre 15-35 años
• **Testículo no descendido** (criptorquidia)
• **Historia familiar** de cáncer testicular
• **Historia personal** – Mayor riesgo en el otro testículo

**Síntomas**
El signo más común es un bulto o hinchazón indolora en cualquiera de los testículos. Otros síntomas incluyen:
• Sensación de pesadez en el escroto
• Dolor sordo en el abdomen inferior o la ingle
• Acumulación repentina de líquido en el escroto
• Dolor o molestia en un testículo

**Cómo Revisarse**
Los autoexámenes mensuales pueden ayudar a detectar el cáncer temprano:
1. Hágalo durante o después de una ducha tibia
2. Sostenga un testículo entre sus pulgares y dedos
3. Ruédelo suavemente para sentir cualquier bulto duro
4. Repita con el otro testículo

**Diagnóstico**
• **Ultrasonido** – Primera prueba para examinar un bulto
• **Análisis de sangre** – Marcadores tumorales
• **Cirugía (orquiectomía)** – Extirpación del testículo afectado

**Tratamiento**
• **Cirugía** – Extirpación del testículo afectado
• **Vigilancia** – Monitoreo cercano después de la cirugía
• **Quimioterapia** – Muy efectiva para el cáncer testicular
• **Radiación** – Para ciertos tipos`
    },
    keyPoints: {
      en: [
        'Most common cancer in young men (15-35 years)',
        'Over 95% survival rate – one of the most curable cancers',
        'Monthly self-exams help detect it early',
        'Painless lump in the testicle is the main warning sign',
        'Treatment usually preserves fertility and sexual function'
      ],
      es: [
        'El cáncer más común en hombres jóvenes (15-35 años)',
        'Tasa de supervivencia superior al 95%',
        'Los autoexámenes mensuales ayudan a detectarlo temprano',
        'Un bulto indoloro en el testículo es la principal señal',
        'El tratamiento generalmente preserva la fertilidad'
      ]
    }
  },
  {
    id: 'upper-tract-urothelial',
    title: {
      en: 'Upper Tract Urothelial Cancer',
      es: 'Cáncer Urotelial del Tracto Superior'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/1/18/Illu_urinary_system.jpg',
      alt: {
        en: 'Diagram of the urinary system',
        es: 'Diagrama del sistema urinario'
      },
      source: 'Wikimedia Commons (Public Domain)',
      caption: {
        en: 'The urinary system showing kidneys, ureters, bladder, and urethra',
        es: 'El sistema urinario mostrando riñones, uréteres, vejiga y uretra'
      }
    },
    tags: ['utuc', 'upper tract', 'renal pelvis', 'ureter', 'urothelial'],
    relatedTopics: ['kidney', 'ureter', 'bladder-cancer'],
    content: {
      en: `Upper tract urothelial carcinoma (UTUC) is a cancer that develops in the lining of the upper urinary tract – the renal pelvis (where the kidney connects to the ureter) or the ureter (the tube that carries urine from the kidney to the bladder).

This cancer is related to bladder cancer and develops from the same type of cells (urothelial cells). It is relatively rare, making up only 5-7% of all urothelial cancers.

**Who Is at Risk?**
• **Smoking** – The biggest risk factor
• **Previous bladder cancer** – About 2-4% of bladder cancer patients develop UTUC
• **Certain medications** (phenacetin, aristolochic acid)
• **Lynch syndrome** (hereditary cancer syndrome)
• **Chronic urinary tract infections**
• **Occupational exposure** to chemicals

**Symptoms**
• Blood in the urine (most common symptom)
• Flank or side pain
• A mass that can be felt in the abdomen
• Unexplained weight loss
• Frequent urinary tract infections

**Diagnosis**
• **CT urogram** – Special imaging of the urinary tract
• **Ureteroscopy** – A thin scope is passed up to directly see the upper tract
• **Urine tests** – Looking for cancer cells
• **Biopsy** – Taking tissue samples during ureteroscopy

**Treatment Options**
Treatment depends on the tumor grade, location, and your kidney function:
• **Radical nephroureterectomy** – Removing the kidney, ureter, and part of the bladder (standard treatment for high-risk tumors)
• **Kidney-sparing surgery** – Using a scope to remove tumors while preserving the kidney (for low-grade tumors or patients with one kidney)
• **Chemotherapy** – May be given before or after surgery
• **Immunotherapy** – For advanced disease

**Important Note**
About 30-50% of patients with UTUC will develop bladder cancer at some point. Regular cystoscopy (bladder examination) is essential for monitoring.`,
      es: `El carcinoma urotelial del tracto superior (UTUC) es un cáncer que se desarrolla en el revestimiento del tracto urinario superior: la pelvis renal (donde el riñón se conecta con el uréter) o el uréter (el tubo que transporta la orina del riñón a la vejiga).

Este cáncer está relacionado con el cáncer de vejiga y se desarrolla a partir del mismo tipo de células (células uroteliales). Es relativamente raro, representando solo el 5-7% de todos los cánceres uroteliales.

**¿Quién Está en Riesgo?**
• **Fumar** – El factor de riesgo más grande
• **Cáncer de vejiga previo** – Alrededor del 2-4% de los pacientes con cáncer de vejiga desarrollan UTUC
• **Ciertos medicamentos**
• **Síndrome de Lynch**
• **Infecciones urinarias crónicas**

**Síntomas**
• Sangre en la orina (síntoma más común)
• Dolor en el costado
• Una masa palpable en el abdomen
• Pérdida de peso inexplicable

**Diagnóstico**
• **Urografía por TC** – Imágenes especiales del tracto urinario
• **Ureteroscopia** – Un endoscopio para ver directamente el tracto superior
• **Biopsia** – Toma de muestras de tejido

**Opciones de Tratamiento**
• **Nefroureterectomía radical** – Extirpación del riñón, uréter y parte de la vejiga
• **Cirugía que preserva el riñón** – Para tumores de bajo grado
• **Quimioterapia** – Puede administrarse antes o después de la cirugía
• **Inmunoterapia** – Para enfermedad avanzada

**Nota Importante**
Alrededor del 30-50% de los pacientes con UTUC desarrollarán cáncer de vejiga. El monitoreo regular con cistoscopia es esencial.`
    },
    keyPoints: {
      en: [
        'Cancer of the renal pelvis or ureter (upper urinary tract)',
        'Related to bladder cancer – regular bladder monitoring needed',
        'Blood in urine is the most common symptom',
        'Smoking is the main preventable risk factor',
        'Treatment usually involves removing the kidney and ureter'
      ],
      es: [
        'Cáncer de la pelvis renal o uréter (tracto urinario superior)',
        'Relacionado con el cáncer de vejiga – necesita monitoreo regular',
        'La sangre en la orina es el síntoma más común',
        'Fumar es el principal factor de riesgo prevenible',
        'El tratamiento generalmente implica extirpar el riñón y el uréter'
      ]
    }
  },
  {
    id: 'urethral-cancer',
    title: {
      en: 'Urethral Cancer',
      es: 'Cáncer de Uretra'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/Blausen_0402_FemaleBladder_02.png/600px-Blausen_0402_FemaleBladder_02.png',
      alt: {
        en: 'Diagram showing the bladder and urethra',
        es: 'Diagrama que muestra la vejiga y la uretra'
      },
      source: 'Blausen Medical / Wikimedia Commons (CC BY 3.0)',
      caption: {
        en: 'The urethra is the tube that carries urine from the bladder out of the body',
        es: 'La uretra es el tubo que transporta la orina desde la vejiga hacia afuera'
      }
    },
    tags: ['urethral', 'urethra', 'rare cancer'],
    relatedTopics: ['urethra', 'bladder-cancer'],
    content: {
      en: `Urethral cancer is a rare type of cancer that forms in the tissues of the urethra, the tube that carries urine from the bladder out of the body. It accounts for less than 1-2% of all urologic cancers.

Because of its rarity, urethral cancer is often diagnosed at a later stage when symptoms become noticeable.

**Who Is at Risk?**
• **Age** – More common in people over 60
• **Gender** – More common in women
• **Previous bladder cancer**
• **Chronic urinary tract infections or inflammation**
• **HPV infection** (certain strains)
• **History of sexually transmitted infections**
• **Urethral strictures** (narrowing of the urethra)

**Symptoms**
Early symptoms may mimic common urinary problems:
• Blood in the urine
• Weak or interrupted urine flow
• Frequent urination, often with little output
• Difficulty starting urination
• Discharge from the urethra
• A lump or mass in the groin, perineum, or penis
• Urinary incontinence
• Pain or burning during urination
• Swollen lymph nodes in the groin

**Diagnosis**
Because symptoms are similar to UTIs or other conditions, diagnosis requires:
• **Physical examination** – Checking for lumps or masses
• **Cystoscopy and urethroscopy** – Looking inside the urethra and bladder with a scope
• **Biopsy** – Taking tissue samples to confirm cancer
• **Imaging** – CT, MRI, or PET scans to see if cancer has spread

**Treatment Options**
Treatment depends on location, stage, and type of cancer:
• **Surgery** – Removing the tumor; may range from local excision to more extensive surgery
• **Radiation therapy** – May be used alone or with chemotherapy
• **Chemotherapy** – Often combined with radiation for better results
• **Immunotherapy** – Newer treatments for advanced disease

Treatment plans are highly individualized due to the rarity of this cancer. Seek care at a center with experience treating urologic cancers.`,
      es: `El cáncer de uretra es un tipo raro de cáncer que se forma en los tejidos de la uretra, el tubo que transporta la orina desde la vejiga hacia afuera del cuerpo. Representa menos del 1-2% de todos los cánceres urológicos.

**¿Quién Está en Riesgo?**
• **Edad** – Más común en personas mayores de 60
• **Género** – Más común en mujeres
• **Cáncer de vejiga previo**
• **Infecciones urinarias crónicas**
• **Infección por VPH**
• **Estrechamientos uretrales**

**Síntomas**
• Sangre en la orina
• Flujo de orina débil o interrumpido
• Micción frecuente, a menudo con poca salida
• Dificultad para comenzar a orinar
• Secreción de la uretra
• Un bulto o masa en la ingle, perineo o pene
• Incontinencia urinaria
• Dolor o ardor al orinar
• Ganglios linfáticos inflamados en la ingle

**Diagnóstico**
• **Examen físico** – Verificación de bultos o masas
• **Cistoscopia y uretroscopia** – Mirar dentro de la uretra y vejiga
• **Biopsia** – Toma de muestras de tejido
• **Imágenes** – TC, resonancia magnética o PET

**Opciones de Tratamiento**
• **Cirugía** – Extirpación del tumor
• **Radioterapia** – Puede usarse sola o con quimioterapia
• **Quimioterapia** – A menudo combinada con radiación
• **Inmunoterapia** – Tratamientos más nuevos para enfermedad avanzada`
    },
    keyPoints: {
      en: [
        'Very rare cancer (less than 1-2% of urologic cancers)',
        'Symptoms often mimic UTI or other common conditions',
        'More common in women and those with prior bladder cancer',
        'Seek care at specialized cancer centers',
        'Treatment is highly individualized based on location and stage'
      ],
      es: [
        'Cáncer muy raro (menos del 1-2% de los cánceres urológicos)',
        'Los síntomas a menudo imitan infecciones urinarias',
        'Más común en mujeres y personas con cáncer de vejiga previo',
        'Busque atención en centros especializados',
        'El tratamiento es altamente individualizado'
      ]
    }
  },
  {
    id: 'penile-cancer',
    title: {
      en: 'Penile Cancer',
      es: 'Cáncer de Pene'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/Penile_cancer_by_hariadhi.svg/400px-Penile_cancer_by_hariadhi.svg.png',
      alt: {
        en: 'Diagram illustrating penile cancer',
        es: 'Diagrama que ilustra el cáncer de pene'
      },
      source: 'Hariadhi / Wikimedia Commons (CC BY-SA 4.0)',
      caption: {
        en: 'Penile cancer most commonly develops on the glans (tip) or foreskin',
        es: 'El cáncer de pene se desarrolla más comúnmente en el glande o prepucio'
      }
    },
    tags: ['penile', 'penis', 'hpv', 'squamous cell'],
    relatedTopics: ['genital-warts'],
    content: {
      en: `Penile cancer is a rare cancer that develops on the skin or in the tissues of the penis. It most commonly affects men over age 60. When found early, it has an excellent survival rate of about 85% at five years.

Most penile cancers (95%) are squamous cell carcinomas that start in the flat skin cells on the tip (glans) or foreskin of the penis.

**Who Is at Risk?**
• **HPV infection** – Human papillomavirus causes about half of all penile cancers
• **Not being circumcised** – Slightly higher risk
• **Poor hygiene** – Buildup of smegma under the foreskin
• **Smoking**
• **Phimosis** – Inability to retract the foreskin
• **Age** – More common after age 60
• **Weakened immune system** (HIV, immunosuppressive medications)

**Symptoms**
Skin changes on the penis are usually the first sign:
• A lump, sore, or ulcer on the penis that doesn't heal
• Reddish, velvety rash
• Small crusty bumps
• Flat, bluish-brown growths
• Changes in skin color or texture
• Foul-smelling discharge under the foreskin
• Swelling at the tip of the penis

As cancer advances:
• Bleeding from the lesion
• Lumps in the groin (spread to lymph nodes)
• Pain
• Difficulty urinating

**Diagnosis**
• **Physical examination** – Careful inspection of the penis
• **Biopsy** – Removing a small piece of tissue to check for cancer
• **Imaging** – CT, MRI, or PET scans if spread is suspected

**Treatment**
Treatment aims to remove cancer while preserving as much function as possible:
• **Topical treatments** – Creams for very early, surface lesions
• **Laser surgery** – For small tumors
• **Circumcision** – If cancer is only on the foreskin
• **Partial penectomy** – Removing only the affected part
• **Total penectomy** – Removing the entire penis (rare, for advanced cases)
• **Lymph node removal** – If cancer has spread to the groin
• **Radiation and chemotherapy** – For advanced disease

**Prevention**
• **HPV vaccination** is highly effective at preventing the HPV strains that cause most penile cancers
• **Good hygiene** – Regular cleaning under the foreskin
• **Don't smoke**
• See a doctor promptly for any new growths or changes on the penis`,
      es: `El cáncer de pene es un cáncer raro que se desarrolla en la piel o los tejidos del pene. Más comúnmente afecta a hombres mayores de 60 años. Cuando se detecta temprano, tiene una excelente tasa de supervivencia de aproximadamente el 85% a cinco años.

**¿Quién Está en Riesgo?**
• **Infección por VPH** – El virus del papiloma humano causa aproximadamente la mitad de todos los cánceres de pene
• **No estar circuncidado** – Riesgo ligeramente mayor
• **Mala higiene** – Acumulación de esmegma bajo el prepucio
• **Fumar**
• **Fimosis** – Incapacidad para retraer el prepucio
• **Edad** – Más común después de los 60 años

**Síntomas**
Los cambios en la piel del pene suelen ser la primera señal:
• Un bulto, llaga o úlcera que no sana
• Sarpullido rojizo aterciopelado
• Pequeños bultos con costras
• Cambios en el color o textura de la piel
• Secreción maloliente bajo el prepucio
• Hinchazón en la punta del pene

**Diagnóstico**
• **Examen físico**
• **Biopsia** – Extracción de una pequeña muestra de tejido
• **Imágenes** – TC, resonancia magnética o PET

**Tratamiento**
• **Tratamientos tópicos** – Cremas para lesiones muy tempranas
• **Cirugía láser** – Para tumores pequeños
• **Circuncisión** – Si el cáncer está solo en el prepucio
• **Penectomía parcial** – Extirpación solo de la parte afectada
• **Penectomía total** – Extirpación del pene completo (raro)
• **Extirpación de ganglios linfáticos**
• **Radiación y quimioterapia** – Para enfermedad avanzada

**Prevención**
• **La vacunación contra el VPH** es muy efectiva
• **Buena higiene** – Limpieza regular bajo el prepucio
• **No fumar**`
    },
    keyPoints: {
      en: [
        'Rare cancer with 85% survival rate when caught early',
        'HPV causes about half of all penile cancers',
        'HPV vaccination can help prevent it',
        'Any new growth or sore on the penis should be evaluated',
        'Treatment aims to preserve function when possible'
      ],
      es: [
        'Cáncer raro con tasa de supervivencia del 85% cuando se detecta temprano',
        'El VPH causa aproximadamente la mitad de los cánceres de pene',
        'La vacunación contra el VPH puede ayudar a prevenirlo',
        'Cualquier nuevo crecimiento o llaga debe ser evaluado',
        'El tratamiento busca preservar la función cuando sea posible'
      ]
    }
  },
  {
    id: 'adrenal-cancer',
    title: {
      en: 'Adrenal Cancer',
      es: 'Cáncer de Glándula Suprarrenal'
    },
    image: {
      url: 'https://upload.wikimedia.org/wikipedia/commons/1/14/Illu_adrenal_gland.jpg',
      alt: {
        en: 'Diagram of the adrenal glands',
        es: 'Diagrama de las glándulas suprarrenales'
      },
      source: 'Wikimedia Commons (Public Domain)',
      caption: {
        en: 'The adrenal glands sit on top of each kidney and produce vital hormones',
        es: 'Las glándulas suprarrenales están encima de cada riñón y producen hormonas vitales'
      }
    },
    tags: ['adrenal', 'adrenocortical', 'adrenal gland', 'hormone'],
    relatedTopics: ['kidney'],
    content: {
      en: `Adrenal cancer, also known as adrenocortical carcinoma (ACC), is a rare cancer that develops in the outer layer (cortex) of the adrenal glands. These small glands sit on top of each kidney and produce important hormones including cortisol, aldosterone, and sex hormones.

Because adrenal cancer is so rare (about 1-2 cases per million people), it is often diagnosed at an advanced stage.

**Who Is at Risk?**
• **Genetic conditions** – Li-Fraumeni syndrome, Beckwith-Wiedemann syndrome, MEN1, Lynch syndrome
• **Age** – Can occur at any age, with peaks in children under 5 and adults 40-50
• No clear lifestyle risk factors have been identified

**Symptoms**
Symptoms depend on whether the tumor produces excess hormones:

**Functioning tumors** (produce hormones) may cause:
• **Cushing syndrome** (excess cortisol):
  - Weight gain in face, neck, and trunk
  - Round "moon face"
  - Fat pad on upper back ("buffalo hump")
  - High blood sugar and blood pressure
  - Muscle weakness
  - Easy bruising
• **Excess androgens/estrogens**:
  - Facial hair growth in women
  - Deepened voice
  - Early puberty in children
  - Breast enlargement in men

**Non-functioning tumors** may only cause:
• Abdominal or back pain
• Feeling of fullness
• A lump in the abdomen
• Unexplained weight loss

**Diagnosis**
• **Blood and urine tests** – Measuring hormone levels
• **Imaging** – CT, MRI, or PET scans
• **Large tumor size** – Tumors larger than 4-6 cm raise suspicion for cancer
• **Biopsy** – Usually done after surgery, not before

**Treatment**
• **Surgery (adrenalectomy)** – The main treatment; removing the affected adrenal gland
• **Mitotane** – A medication that kills adrenal cancer cells and controls hormone production
• **Chemotherapy** – For advanced or recurrent cancer
• **Radiation therapy** – Sometimes used after surgery or to control symptoms

**Prognosis**
Outcomes depend heavily on the stage at diagnosis. Early-stage tumors that can be completely removed have better outcomes. Adrenal cancer can be aggressive and may recur, so regular follow-up is essential.`,
      es: `El cáncer suprarrenal, también conocido como carcinoma adrenocortical (CAC), es un cáncer raro que se desarrolla en la capa externa (corteza) de las glándulas suprarrenales. Estas pequeñas glándulas se encuentran sobre cada riñón y producen hormonas importantes.

**¿Quién Está en Riesgo?**
• **Condiciones genéticas** – Síndrome de Li-Fraumeni, síndrome de Beckwith-Wiedemann
• **Edad** – Puede ocurrir a cualquier edad

**Síntomas**
Los síntomas dependen de si el tumor produce exceso de hormonas:

**Tumores funcionantes** pueden causar:
• **Síndrome de Cushing** (exceso de cortisol):
  - Aumento de peso en cara, cuello y tronco
  - Cara redonda "de luna"
  - Almohadilla de grasa en la parte superior de la espalda
  - Azúcar y presión arterial altas
  - Debilidad muscular
• **Exceso de andrógenos/estrógenos**:
  - Crecimiento de vello facial en mujeres
  - Voz más grave
  - Pubertad temprana en niños

**Tumores no funcionantes** pueden causar solo:
• Dolor abdominal o de espalda
• Sensación de plenitud
• Un bulto en el abdomen
• Pérdida de peso inexplicable

**Diagnóstico**
• **Análisis de sangre y orina** – Midiendo niveles hormonales
• **Imágenes** – TC, resonancia magnética o PET
• **Tumores mayores de 4-6 cm** aumentan la sospecha de cáncer

**Tratamiento**
• **Cirugía (adrenalectomía)** – El tratamiento principal
• **Mitotano** – Medicamento que mata las células cancerosas suprarrenales
• **Quimioterapia** – Para cáncer avanzado o recurrente
• **Radioterapia** – A veces después de la cirugía`
    },
    keyPoints: {
      en: [
        'Very rare cancer of the adrenal glands',
        'May cause hormone-related symptoms (Cushing syndrome)',
        'Tumors larger than 4-6 cm warrant urgent evaluation',
        'Surgery is the main treatment',
        'Regular follow-up is essential due to recurrence risk'
      ],
      es: [
        'Cáncer muy raro de las glándulas suprarrenales',
        'Puede causar síntomas relacionados con hormonas',
        'Los tumores mayores de 4-6 cm requieren evaluación urgente',
        'La cirugía es el tratamiento principal',
        'El seguimiento regular es esencial'
      ]
    }
  }
];

// Export list of article IDs for routing
export const urologicCancerIds = urologicCancerArticles.map(a => a.id);
